/**
 * Purpose: Shared transcription / voice-documentation helpers used by visit lists, whiteboard, calendar popup, and the recording modal.
 */

export const VOICE_DOC_CHECKLIST_TOTAL = 8;

export const VISIT_DOC_CHECKLIST_SEED = [
  { id: "cc", label: "Chief Complaint", done: false, active: false },
  { id: "hx", label: "History & ROS", done: false, active: false },
  { id: "exam", label: "Physical Examination", done: false, active: false },
  { id: "orders", label: "Orders & Referrals", done: false, active: false },
  { id: "assess", label: "Assessment & Dx", done: false, active: false },
  { id: "proc", label: "Procedures Performed", done: false, active: false },
  { id: "work", label: "Work Restrictions", done: false, active: false },
  { id: "dc", label: "Plan & Follow-Up", done: false, active: false },
];

/**
 * Purpose: Converts stored Quill/HTML or plain text to a single trimmed line for placeholder checks.
 * Inputs: htmlOrPlain (string)
 * Output: plain string (may be empty)
 */
export function stripClinicalFieldToPlain(htmlOrPlain) {
  let s = String(htmlOrPlain ?? "").trim();
  if (!s) return "";
  if (s.includes("<")) {
    s = s
      .replace(/<\/p>/gi, "\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]+>/g, " ");
  }
  return s.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

/**
 * Purpose: Returns true when note field text is more than empty/whitespace or AI boilerplate only.
 * Inputs: value (string — HTML or plain from notes_collection label)
 * Output: boolean
 */
export function isMeaningfulClinicalHtmlOrText(value) {
  const plain = stripClinicalFieldToPlain(value);
  if (!plain) return false;

  if (/^n\/a\.?$/i.test(plain) || /^none\.?$/i.test(plain) || /^no information\.?$/i.test(plain) || /^unknown\.?$/i.test(plain)) {
    return false;
  }

  const lower = plain.toLowerCase();
  if (/\bnot documented(?:\s+in chart)?\b/i.test(lower)) {
    const residual = lower
      .replace(/\bnot documented(?:\s+in chart)?\b\.?/gi, " ")
      .replace(
        /\b(chief|complaint|history|of|present|illness|hpi|review|systems|\bros\b|physical|examination|pe|vitals|general|appearance|assessment|diagnosis|dx|impression|mdm|plan|follow|up|orders|referrals|procedures|performed|work|restrictions|and)\b/gi,
        " "
      )
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (residual.replace(/[^a-z0-9]/g, "").length < 12) return false;
  }

  const alnum = plain.replace(/[^a-z0-9]/gi, "");
  return alnum.length >= 8;
}

/**
 * Purpose: True if any label in a section has meaningful clinical content.
 * Inputs: labels (array of { value })
 * Output: boolean
 */
export function sectionLabelsHaveMeaningfulContent(labels) {
  if (!Array.isArray(labels) || labels.length === 0) return false;
  return labels.some((lab) => isMeaningfulClinicalHtmlOrText(lab?.value));
}

/**
 * Purpose: Strict section completeness gate for checklist ticks.
 * Rules:
 * - aggregated section text must exist
 * - after whitespace strip, length >= 10
 * - must not be placeholder text
 * - must not be only the section heading itself
 */
export function isSectionMeaningful(sectionName, labels) {
  if (!Array.isArray(labels) || labels.length === 0) return false;

  const combined = labels
    .map((lab) => stripClinicalFieldToPlain(lab?.value))
    .filter(Boolean)
    .join(" ")
    .trim();
  if (!combined) return false;

  const normalized = combined.toLowerCase();
  const compact = normalized.replace(/\s+/g, "");
  if (compact.length < 10) return false;

  const placeholders = [
    "not mentioned",
    "not provided",
    "no information",
    "n/a",
    "none",
    "pending",
    "not documented",
  ];
  if (placeholders.includes(normalized)) return false;

  const heading = String(sectionName || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (heading) {
    const headingCompact = heading.replace(/\s+/g, "");
    if (headingCompact && compact === headingCompact) return false;
  }

  // Reject concatenated placeholder blobs like:
  // "Chief Complaint ... Not documented ... Physical Exam ... Not documented ..."
  if (isPlaceholderOnlyBlob(normalized)) return false;

  // Reject heading-only narration (e.g., "chief complaint, history, exam, assessment...").
  if (isLikelyHeadingOnlyContent(normalized, heading)) return false;

  return true;
}

function isPlaceholderOnlyBlob(normalizedText) {
  const headingWords = [
    "chief", "complaint", "history", "hpi", "present", "illness", "review", "ros", "systems",
    "physical", "exam", "examination", "assessment", "dx", "diagnosis", "plan", "orders",
    "order", "results", "referrals", "referral", "procedures", "procedure", "performed",
    "work", "restrictions", "restriction", "follow", "followup", "clinical", "summary",
  ];

  let residual = normalizedText;
  residual = residual.replace(/\bnot documented\b/g, " ");
  residual = residual.replace(/\bnot mentioned\b/g, " ");
  residual = residual.replace(/\bnot provided\b/g, " ");
  residual = residual.replace(/\bno information\b/g, " ");
  residual = residual.replace(/\bn\/a\b/g, " ");
  residual = residual.replace(/\bnone\b/g, " ");
  residual = residual.replace(/\bpending\b/g, " ");
  residual = residual.replace(new RegExp(`\\b(${headingWords.join("|")})\\b`, "g"), " ");
  residual = residual.replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

  // If nothing clinically substantive remains, section is not meaningful.
  return residual.length === 0;
}

function isLikelyHeadingOnlyContent(normalizedText, sectionHeading = "") {
  const headingTerms = new Set([
    "chief", "complaint", "history", "ros", "review", "systems", "physical", "exam", "examination",
    "orders", "order", "referrals", "referral", "assessment", "dx", "diagnosis", "procedures",
    "procedure", "performed", "work", "restrictions", "restriction", "plan", "follow", "followup",
    "results", "result", "clinical", "summary",
  ]);
  const fillerTerms = new Set([
    "and", "or", "with", "for", "the", "a", "an", "to", "of", "in", "on", "is", "was",
    "done", "completed", "mentioned", "provided", "included", "including", "planned", "followed",
    "not", "documented",
  ]);

  const tokens = normalizedText
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const headingTokenCount = tokens.filter((t) => headingTerms.has(t)).length;
  const detailTokens = tokens.filter((t) => !headingTerms.has(t) && !fillerTerms.has(t) && t.length >= 3);

  const headingCompact = sectionHeading.replace(/\s+/g, "");
  const noPunctCompact = normalizedText.replace(/[^a-z0-9]/g, "");
  if (headingCompact && noPunctCompact === headingCompact) {
    return true;
  }

  // If content is mostly headings and has almost no concrete clinical details, treat as non-meaningful.
  if (headingTokenCount >= 3 && detailTokens.length < 2) {
    return true;
  }

  return false;
}

/**
 * Purpose: Maps one AI sectionName to a single static checklist id (first match by priority).
 * Inputs: sectionName (string from structured AI response)
 * Output: checklist key (cc|hx|exam|orders|assess|proc|work|dc) or null
 */
export function primaryChecklistIdForAiSection(sectionName) {
  const s = String(sectionName || "").toLowerCase();
  const rules = [
    [/chief complaint|\bcc\b|reason for visit/, "cc"],
    [/history of present|hpi|present illness|history|review of systems|\bros\b|past medical|social history|family history/, "hx"],
    [/physical exam|examination|\bpe\b|vitals|general appearance/, "exam"],
    [/order|referral|lab\b|imaging|prescription|medication|rx\b/, "orders"],
    [/assessment|diagnosis|impression|\bdx\b|mdm|medical decision|differential/, "assess"],
    [/procedure|performed|intervention|treatment rendered/, "proc"],
    [/work restriction|restricted duty|disability|accommodation|fit for duty/, "work"],
    [/plan|follow|instruction|disposition|patient instruction|counseling|education/, "dc"],
  ];
  for (const [re, id] of rules) {
    if (re.test(s)) return id;
  }
  return null;
}

/**
 * Purpose: All static ids an AI section might touch (for checklist "done" ticks); looser than primary bucket.
 * Inputs: sectionName (string)
 * Output: string[] unique checklist ids
 */
export function matchAiSectionToChecklistIds(sectionName) {
  const s = String(sectionName || "").toLowerCase();
  const ids = new Set();
  if (/(chief complaint|\bcc\b|reason for visit)/i.test(s)) ids.add("cc");
  if (/(hpi|history|present illness|review of systems|\bros\b|pmh|social|family)/i.test(s)) ids.add("hx");
  if (/(physical exam|examination|\bpe\b|vitals)/i.test(s)) ids.add("exam");
  if (/(order|referral|lab|imaging|prescription)/i.test(s)) ids.add("orders");
  if (/(assessment|diagnosis|impression|\bdx\b|mdm)/i.test(s)) ids.add("assess");
  if (/(procedure|performed|intervention)/i.test(s)) ids.add("proc");
  if (/(work restriction|duty|disability|accommodation)/i.test(s)) ids.add("work");
  if (/(plan|follow|instruction|disposition)/i.test(s)) ids.add("dc");
  return Array.from(ids);
}

/**
 * Purpose: Marks static checklist items when merged notes contain matching sections with meaningful content.
 * Inputs: notesCollection (API notes_collection array)
 * Output: checklist rows aligned to VISIT_DOC_CHECKLIST_SEED
 */
export function buildChecklistFromNotes(notesCollection) {
  const doneIds = new Set();
  if (Array.isArray(notesCollection)) {
    for (const sec of notesCollection) {
      const labels = sec?.label || [];
      if (!isSectionMeaningful(sec?.sectionName || "", labels)) continue;
      for (const id of matchAiSectionToChecklistIds(sec?.sectionName || "")) {
        doneIds.add(id);
      }
    }
  }
  return VISIT_DOC_CHECKLIST_SEED.map((row) => ({
    ...row,
    done: doneIds.has(row.id),
  }));
}

/**
 * Purpose: Computes checklist completion from structured AI note content only.
 * Inputs: notesCollection, transcriptSegments ({ text }[])
 * Output: checklist rows aligned to VISIT_DOC_CHECKLIST_SEED
 */
export function buildChecklistFromNotesAndTranscripts(notesCollection, transcriptSegments) {
  void transcriptSegments;
  const doneIds = new Set();

  if (Array.isArray(notesCollection)) {
    for (const sec of notesCollection) {
      const labels = sec?.label || [];
      if (!isSectionMeaningful(sec?.sectionName || "", labels)) continue;
      const labelBlob = labels
        .map((lab) => stripClinicalFieldToPlain(lab?.value))
        .filter(Boolean)
        .join(" ");
      // Keep existing sectionName mapping and extend with content extraction when AI combines sections.
      const mappingText = `${String(sec?.sectionName || "")} ${labelBlob}`.trim();
      for (const id of matchAiSectionToChecklistIds(mappingText)) {
        doneIds.add(id);
      }
    }
  }

  // Keep existing checklist seed + mapping behavior; only done-condition is stricter/notes-only.
  return VISIT_DOC_CHECKLIST_SEED.map((row) => ({
    ...row,
    done: doneIds.has(row.id),
  }));
}

/**
 * Purpose: Computes progress tier from merged AI note and transcript segments, same as modal checklist source.
 * Inputs: notesCollection (array|null), transcriptSegments (array|null)
 * Output: { tier: 'none'|'progress'|'complete', doneCount, total }
 */
export function getVoiceDocProgressFromSources(notesCollection, transcriptSegments) {
  const checklist = buildChecklistFromNotesAndTranscripts(notesCollection, transcriptSegments);
  const doneCount = checklist.filter((r) => r.done).length;
  const total = checklist.length;
  let tier = "none";
  if (total > 0 && doneCount >= total) tier = "complete";
  else if (doneCount > 0) tier = "progress";
  return { tier, doneCount, total };
}

/**
 * Reads voice checklist snapshot included on employee-visits list rows (no per-visit audio API calls).
 */
export function voiceDocProgressFromVisitListRow(visit) {
  const categoryForRedirect = visit?.screen_type || visit?.visit_category;
  if (!isVoiceTranscriptionListCategory(categoryForRedirect)) {
    return null;
  }
  if (
    visit?.voice_doc_tier != null &&
    visit?.voice_doc_done_count != null &&
    visit?.voice_doc_total != null
  ) {
    return {
      tier: visit.voice_doc_tier,
      doneCount: Number(visit.voice_doc_done_count) || 0,
      total: Number(visit.voice_doc_total) || VOICE_DOC_CHECKLIST_TOTAL,
    };
  }
  return {
    tier: "none",
    doneCount: 0,
    total: VOICE_DOC_CHECKLIST_TOTAL,
  };
}

/**
 * Purpose: Maps progress to toolbar icon type and tooltip title (visits list + meatball).
 * Inputs: progress — return shape of getVoiceDocProgressFromSources, or null
 * Output: { iconType: string, title: string }
 */
export function getVoiceToolbarUi(progress) {
  const p =
    progress && typeof progress === "object"
      ? progress
      : { tier: "none", doneCount: 0, total: VOICE_DOC_CHECKLIST_TOTAL };
  const total = typeof p.total === "number" ? p.total : VOICE_DOC_CHECKLIST_TOTAL;
  const doneCount = typeof p.doneCount === "number" ? p.doneCount : 0;
  if (p.tier === "complete") {
    return {
      iconType: "VoiceDocComplete",
      title: `Voice documentation complete — all ${total} visit note sections have content. Click to open.`,
    };
  }
  if (p.tier === "progress") {
    return {
      iconType: "VoiceDocInProgress",
      title: `Voice documentation in progress — ${doneCount} of ${total} sections documented. Click to continue.`,
    };
  }
  return {
    iconType: "Voice",
    title: "Start transcription — record visit audio and generate the clinical note.",
  };
}

/**
 * Purpose: True when visit uses the voice-first transcription row action (Work Comp New Injury / Physical Therapy).
 * Inputs: categoryForRedirect — visit.screen_type || visit.visit_category
 * Output: boolean
 */
export function isVoiceTranscriptionListCategory(categoryForRedirect) {
  const c = String(categoryForRedirect || "");
  return c === "Work Comp - New Injury" || c === "Work Comp - Physical Therapy";
}
